import React, { useState } from 'react';
import './GanttChart.scss';
import Text         from '../../Atomsv2/Text/Text';
import GanttToolbar    from './GanttToolbar';
import GanttDateHeader from './GanttDateHeader';
import GanttRow        from './GanttRow';

// píxeles por día para cada escala
const PX_POR_DIA = { dia: 40, semana: 14, mes: 5, trimestre: 2 };

// nombres de días en español (índice = d.getDay())
const DIAS_ES = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

// etiquetas de trimestre en español con su rango de meses (índice = 0-3)
const TRIMESTRES = ['Ene–Mar', 'Abr–Jun', 'Jul–Sep', 'Oct–Dic'];

// número de semana ISO (1-53) de una fecha
const semanaISO = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const inicio = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - inicio) / 86400000) + 1) / 7);
};

/*
 * GanttChart — Diagrama de Gantt genérico y reutilizable.
 *
 * Props:
 *   rows  array  Lista de filas. Ver GanttRow.jsx para la forma de cada objeto.
 */
const GanttChart = ({ rows = [],onClick }) => {
    const [escala, setEscala] = useState('semana');
    const pxDia = PX_POR_DIA[escala];

    // Solo las filas que tienen fechas para calcular el rango del canvas
    const rowsConFecha = rows.filter(r => r.start && r.end);

    if (rowsConFecha.length === 0) {
        return (
            <div className="GanttChart GanttChart--empty">
                <span className="icon" data-icon="event_busy" />
                <Text color="var(--gray-dark-400)">Sin elementos con fechas para mostrar</Text>
            </div>
        );
    }

    // ── Rango de fechas ───────────────────────────────────────────────────────
    const todasLasFechas = rowsConFecha.flatMap(r => [
        new Date(r.start.split('T')[0]),
        new Date(r.end.split('T')[0]),
    ]);
    const tsMin = Math.min(...todasLasFechas.map(d => d.getTime()));
    const tsMax = Math.max(...todasLasFechas.map(d => d.getTime()));

    const fechaInicio = new Date(tsMin - 3 * 86400000);
    const fechaFin    = new Date(tsMax + 14 * 86400000);
    fechaInicio.setHours(0, 0, 0, 0);
    fechaFin.setHours(0, 0, 0, 0);

    const diasRange = [];
    const dCursor = new Date(fechaInicio);
    while (dCursor <= fechaFin) {
        diasRange.push(new Date(dCursor));
        dCursor.setDate(dCursor.getDate() + 1);
    }

    const totalAncho = diasRange.length * pxDia;

    // ── Helpers de posición (se pasan como funciones a cada GanttRow) ─────────
    const xDe = (fechaStr) => {
        const d = new Date(fechaStr.split('T')[0]);
        d.setHours(0, 0, 0, 0);
        return Math.max(0, Math.round((d - fechaInicio) / 86400000) * pxDia);
    };

    const anchoDe = (ini, fin) => {
        const d1 = new Date(ini.split('T')[0]);
        const d2 = new Date(fin.split('T')[0]);
        const dias = Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
        return dias * pxDia;
    };

    // ── Marcador de hoy ───────────────────────────────────────────────────────
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const hoyX   = Math.round((hoy - fechaInicio) / 86400000) * pxDia;
    const hoyVis = hoyX >= 0 && hoyX <= totalAncho;

    // ── Columnas del header según la escala ───────────────────────────────────
    const colsHeader = [];

    if (escala === 'dia') {
        for (const d of diasRange) {
            colsHeader.push({
                label: d.getDate(),
                sub:   DIAS_ES[d.getDay()],
                width: pxDia,
                fds:   d.getDay() === 0 || d.getDay() === 6,
                esHoy: d.getTime() === hoy.getTime(),
            });
        }
    }

    // Agrupación de meses — para escala día y semana (cabecera doble)
    const colsHeaderMonth = (escala === 'dia' || escala === 'semana') ? (() => {
        const groups = [];
        let mi = 0;
        while (mi < diasRange.length) {
            const d = diasRange[mi];
            const mes = d.getMonth();
            let mj = mi;
            while (mj < diasRange.length && diasRange[mj].getMonth() === mes) mj++;
            groups.push({
                label: d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }),
                width: (mj - mi) * pxDia,
            });
            mi = mj;
        }
        return groups;
    })() : null;

    if (escala === 'semana') {
        let i = 0;
        while (i < diasRange.length) {
            const d   = diasRange[i];
            // días hasta el siguiente lunes (alinea grupos con semanas ISO reales)
            const dow = d.getDay() === 0 ? 7 : d.getDay(); // 1=lun … 7=dom
            const cant = Math.min(8 - dow, diasRange.length - i);
            colsHeader.push({ label: `S${semanaISO(d)}`, width: cant * pxDia });
            i += cant;
        }
    }
    if (escala === 'mes') {
        let i = 0;
        while (i < diasRange.length) {
            const d   = diasRange[i];
            const mes = d.getMonth();
            let j = i;
            while (j < diasRange.length && diasRange[j].getMonth() === mes) j++;
            colsHeader.push({ label: d.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' }), width: (j - i) * pxDia });
            i = j;
        }
    }
    if (escala === 'trimestre') {
        let i = 0;
        while (i < diasRange.length) {
            const d    = diasRange[i];
            const trim = Math.floor(d.getMonth() / 3);
            let j = i;
            while (j < diasRange.length && Math.floor(diasRange[j].getMonth() / 3) === trim) j++;
            colsHeader.push({ label: `${TRIMESTRES[trim]} ${String(d.getFullYear()).slice(2)}`, width: (j - i) * pxDia });
            i = j;
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="GanttChart">
            <GanttToolbar escala={escala} onChange={setEscala} />

            <div className="GanttChart__scroll">
                <GanttDateHeader
                    colsHeader={colsHeader}
                    colsHeaderMonth={colsHeaderMonth}
                    totalAncho={totalAncho}
                    hoyX={hoyX}
                    hoyVisible={hoyVis}
                />

                {rows.map((row, i) => (
                    <GanttRow
                        key={row.id ?? i}
                        row={row}
                        totalAncho={totalAncho}
                        hoyX={hoyX}
                        hoyVisible={hoyVis}
                        xDe={xDe}
                        anchoDe={anchoDe}
                        pxDia={pxDia}
                        onClick={!row.isGroup && onClick ? () => onClick(row) : undefined}
                    />
                ))}
            </div>
        </div>
    );
};

export default GanttChart;
